import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { normalizeCropEntry, normalizeTaskEntry, normalizeReportEntry } from './journal-utils'

export const useJournalEntries = ({ farmId, seasonId }) => {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchEntries() {
      if (!farmId || !seasonId) {
        setEntries([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const { data: plots, error: plotsErr } = await supabaseGetter.organizations.getPlots(farmId)
        if (plotsErr) throw plotsErr

        const plotList = plots || []
        const patchResults = await Promise.all(
          plotList.map(p => supabaseGetter.organizations.getPatches(p.id))
        )
        const allOrgIds = [
          farmId,
          ...plotList.map(p => p.id),
          ...patchResults.flatMap(r => (r.data || []).map(p => p.id)),
        ]

        const [cropRes, taskRes, reportRes] = await Promise.all([
          supabaseGetter.crops.getWithUserByOrganizationsAndSeason(allOrgIds, seasonId),
          supabaseGetter.tasks.getWithUserByOrganizationsAndSeason(allOrgIds, seasonId),
          supabaseGetter.reports.getByOrganizationAndSeason(farmId, seasonId),
        ])

        if (cropRes.error) throw cropRes.error
        if (taskRes.error) throw taskRes.error
        if (reportRes.error) throw reportRes.error

        const normalized = [
          ...(cropRes.data || []).filter(c => c.crop_details?.notes).map(normalizeCropEntry),
          ...(taskRes.data || []).map(normalizeTaskEntry),
          ...(reportRes.data || []).filter(r => r.notes).map(normalizeReportEntry),
        ].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))

        setEntries(normalized)
      } catch (err) {
        console.error('[useJournalEntries]:', err)
        setError(err.message)
        setEntries([])
      } finally {
        setLoading(false)
      }
    }

    fetchEntries()
  }, [farmId, seasonId])

  return { entries, loading, error }
}
